import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MailX, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { Section, Overline } from "../components/Layout";
import Orbs from "../components/Orbs";

/**
 * Opt out of bulk email (ND-14 / FR-COM-4).
 *
 * Unsubscribing happens on load, without a confirm step — the opposite of the
 * cancel page. Someone who clicked "unsubscribe" has already decided, and making
 * them click twice is the pattern that gets senders reported as spam. The undo is
 * offered immediately afterwards for the misclick case.
 *
 * Transactional email (registration confirmations, cancellations, decisions) is
 * unaffected and the copy says so, so nobody opts out fearing they'll lose their
 * own booking receipts.
 */
export default function Unsubscribe() {
  const { token } = useParams();
  const [state, setState] = useState({ phase: "loading" });

  useEffect(() => {
    let live = true;
    api.post("/unsubscribe", { token })
      .then((r) => live && setState({ phase: "done", email: r.data.email }))
      .catch((e) => live && setState({
        phase: "error",
        message: e.response?.data?.detail || "This unsubscribe link is invalid.",
      }));
    return () => { live = false; };
  }, [token]);

  const resubscribe = async () => {
    try {
      await api.post("/unsubscribe/resubscribe", { token });
      setState((s) => ({ ...s, phase: "resubscribed" }));
    } catch {
      setState((s) => ({ ...s, phase: "error", message: "Couldn't undo that." }));
    }
  };

  return (
    <div className="min-h-screen relative">
      <Orbs />
      <Section className="pt-24 pb-16" innerClassName="max-w-lg mx-auto">
        <div className="glass-panel p-8 text-center" data-testid="unsubscribe-panel">
          {state.phase === "loading" && (
            <div className="text-sm text-muted-foreground">Updating your preferences…</div>
          )}

          {state.phase === "error" && (
            <>
              <div className="h-12 w-12 rounded-full mx-auto flex items-center justify-center"
                   style={{ background: "hsl(var(--vd-coral) / 0.14)", color: "hsl(var(--vd-coral))" }}>
                <AlertCircle size={18} />
              </div>
              <div className="mt-3 font-heading text-xl">Something's off</div>
              <div className="text-sm text-muted-foreground mt-1" data-testid="unsubscribe-error">{state.message}</div>
            </>
          )}

          {state.phase === "done" && (
            <>
              <div className="h-12 w-12 rounded-full mx-auto flex items-center justify-center"
                   style={{ background: "hsl(var(--vd-green) / 0.14)", color: "hsl(var(--vd-green))" }}>
                <MailX size={18} />
              </div>
              <Overline className="mt-4">Unsubscribed</Overline>
              <div className="mt-1 font-heading text-2xl leading-tight">You're off the list</div>
              <div className="text-sm text-muted-foreground mt-2" data-testid="unsubscribe-done">
                <span className="font-mono">{state.email}</span> won't receive event
                announcements or community updates from us again.
              </div>
              <div className="mt-4 text-xs text-muted-foreground">
                You'll still get emails about events you personally register for —
                confirmations, decisions and cancellations.
              </div>
              <button onClick={resubscribe} className="text-xs mt-5 hover:underline"
                      style={{ color: "hsl(var(--vd-green))" }} data-testid="unsubscribe-undo">
                Didn't mean to? Undo this
              </button>
            </>
          )}

          {state.phase === "resubscribed" && (
            <>
              <div className="h-12 w-12 rounded-full mx-auto flex items-center justify-center"
                   style={{ background: "hsl(var(--vd-green) / 0.14)", color: "hsl(var(--vd-green))" }}>
                <CheckCircle2 size={18} />
              </div>
              <div className="mt-3 font-heading text-xl">You're back on the list</div>
              <div className="text-sm text-muted-foreground mt-1">
                We'll keep you posted about upcoming events.
              </div>
              <a href="/events" className="btn-secondary inline-flex mt-6">Browse events</a>
            </>
          )}
        </div>
      </Section>
    </div>
  );
}
